import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Panel, Loader, Message, toaster } from 'rsuite';
import PageBase from '../../../components/PageBase';
import { getOrder } from '../../../service/OrderService';
import { createECPayOrder } from '../../../service/PaymentService';
import type { Order, ECPayConfig } from '../../../my-type';
import { authUserData } from '../../../service';

const ECPayCreditCard: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authUser = authUserData();

  // ECPay configuration - you may want to move this to environment variables
  const ecpayConfig: ECPayConfig = {
    merchantID: process.env.REACT_APP_ECPAY_MERCHANT_ID || "3002607", // Replace with your merchant ID
    isProduction: process.env.REACT_APP_ECPAY_ENV === "production"
  };

  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const orderData = await getOrder(orderId!);

      if (!orderData) {
        setError('無法找到訂單資料');
        return;
      }

      setOrder(orderData);
    } catch (err) {
      console.error('Error loading order:', err);
      setError('載入訂單資料時發生錯誤');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (!orderId) {
      setError('訂單ID不能為空');
      setLoading(false);
      return;
    }

    loadOrder();
  }, [orderId, loadOrder]);

  const handlePayment = async () => {
    if (!order) {
      toaster.push(
        <Message type="error">訂單資料不完整</Message>,
        { placement: 'topCenter' }
      );
      return;
    }

    try {
      setProcessing(true);

      const success = await createECPayOrder(
        order.orderID,
        order.rideFee,
        `${authUser?.team.name}乘車服務 - ${order.orderID}`,
        `訂單編號: ${order.orderID}`,
        ecpayConfig,
        {
          clientBackURL: `${window.location.origin}/rider/order`,
          orderResultURL: `${window.location.origin}/rider/order/payment/ecpay/result`,
          language: "CHI",
          remark: `乘客: ${order.passengerName}, 電話: ${order.passengerPhone}`
        }
      );

      if (!success) {
        toaster.push(
          <Message type="error">建立ECPay付款訂單失敗，請稍後再試</Message>,
          { placement: 'topCenter' }
        );
      }
      // If successful, the user will be redirected to ECPay payment page
      // No need to handle success here as the page will redirect
    } catch (err) {
      console.error('Payment creation error:', err);
      toaster.push(
        <Message type="error">建立付款時發生錯誤，請稍後再試</Message>,
        { placement: 'topCenter' }
      );
    } finally {
      setProcessing(false);
    }
  };

  const formatDateTime = (date: Date) => {
    return new Date(date).toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return `NT$ ${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <PageBase
        header={undefined}
        content={
          <Panel header="ECPay 信用卡付款" bordered>
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Loader size="lg" content="載入訂單資料中..." />
            </div>
          </Panel>
        }
        showSide={true}
      />
    );
  }

  if (error || !order) {
    return (
      <PageBase
        header={undefined}
        content={
          <Panel header="ECPay 信用卡付款" bordered>
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Message type="error" header="錯誤">
                {error || '無法載入訂單資料'}
              </Message>
              <br />
              <Button
                appearance="primary"
                onClick={() => navigate('/rider/order')}
              >
                返回訂單列表
              </Button>
            </div>
          </Panel>
        }
        showSide={true}
      />
    );
  }

  return (
    <PageBase
      header={undefined}
      content={
        <div>
          <Panel header="ECPay 信用卡付款" bordered>
            <div style={{ marginBottom: '20px' }}>
              <h5>訂單資訊</h5>
              <hr />
              <div style={{ marginBottom: '10px' }}>
                <strong>訂單編號:</strong> {order.orderID}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>乘客姓名:</strong> {order.passengerName}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>乘客電話:</strong> {order.passengerPhone}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>乘車時間:</strong> {formatDateTime(order.rideTime)}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>上車地點:</strong> {order.address}
              </div>
              {order.terminal && (
                <div style={{ marginBottom: '10px' }}>
                  <strong>航廈:</strong> {order.terminal}
                </div>
              )}
              {order.flightNo && (
                <div style={{ marginBottom: '10px' }}>
                  <strong>航班號碼:</strong> {order.flightNo}
                </div>
              )}
              <div style={{ marginBottom: '10px' }}>
                <strong>乘車費用:</strong>
                <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#007bff' }}>
                  {formatCurrency(order.rideFee)}
                </span>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '30px' }}>
              <Button
                appearance="primary"
                size="lg"
                onClick={handlePayment}
                loading={processing}
                disabled={processing || order.paymentPaid}
                style={{
                  minWidth: '200px',
                  backgroundColor: order.paymentPaid ? '#28a745' : '#007bff'
                }}
              >
                {processing
                  ? '處理中...'
                  : order.paymentPaid
                    ? '已付款'
                    : `使用ECPay付款 ${formatCurrency(order.rideFee)}`
                }
              </Button>

              <br /><br />

              <Button
                appearance="subtle"
                onClick={() => navigate(`/rider/order/${orderId}`)}
                disabled={processing}
              >
                返回訂單詳情
              </Button>
            </div>
          </Panel>

          {order.paymentPaid && (
            <Panel style={{ marginTop: '20px', backgroundColor: '#d4edda', borderColor: '#c3e6cb' }}>
              <Message type="success" header="付款狀態">
                此訂單已完成付款，無需重複付款。
              </Message>
            </Panel>
          )}
        </div>
      }
      showSide={true}
    />
  );
};

export default ECPayCreditCard;
